import * as React from "react";
import useArticleFormModel from "../hooks/useArticleFormModel";
import useModelState from "../hooks/useModelState";
import ArticleForm from "../shared/ArticleForm";
import PreviewButton from "./PreviewButton";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useParams } from "react-router-dom";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

export default function Edit() {
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [article, setArticle] = useArticleFormModel();
  const [modelState, setModelState] = useModelState();
  const { id: stringId } = useParams<{ id: string }>();
  const id = parseInt(stringId!);
  const api = useApi();
  const toastAlert = useToastAlert();

  React.useEffect(() => {
    async function fetchArticle() {
      setLoading(true);
      try {
        const response = await api.admin.get(`api/kb/articles?id=${id}`);
        if (response.status === 404) {
          toastAlert.displayToast(Severity.Error, "Article not found");
          window.location.href = "/kb/articles";
          return;
        }
        const result = await response.json();
        setArticle({
          title: result.Title ?? "",
          slug: result.Slug ?? "",
          topicId: result.TopicId ?? "",
          description: result.Description ?? "",
          content: result.Content ?? "",
          searchTags: result.SearchTags ?? "",
          visibility: result.Visibility ?? 0,
          isPublished: result.IsPublished ?? false,
          url: result.Url ?? "",
          displayPriority: result.DisplayPriority ?? 0,
          botSummary: result.BotSummary ?? "",
          enableBotResponse: result.EnableBotResponse ?? false,
          isFeatured: result.IsFeatured ?? false,
          lastReviewedAt: result.LastReviewedAt ?? null
        });
      } catch (err) {
        console.error("Failed to fetch article:", err);
        toastAlert.displayToast(Severity.Error, "Failed to load article");
      } finally {
        setLoading(false);
      }
    }
    fetchArticle();
  }, [id, setArticle]);

  async function save(publish: boolean) {
    setSaving(true);
    try {
      const resp = await api.admin.put(
        `api/kb/articles?id=${encodeURIComponent(id)}`,
        { ...article, isPublished: publish }
      );
      if (resp.status === 400) {
        toastAlert.displayToast(Severity.Error, "Failed to save article");
        setSaving(false);
        return;
      }
      toastAlert.displayToast(
        Severity.Success,
        publish ? "Article published" : "Draft saved"
      );
      window.location.href = "/kb/articles";
    } catch (err) {
      console.error("Failed to save article:", err);
      toastAlert.displayToast(Severity.Error, "Failed to save article");
      setSaving(false);
    }
  }

  async function deleteArticle() {
    if (confirm("Are you sure you want to delete this article? This cannot be undone.")) {
      try {
        await api.admin.delete(`api/kb/articles?id=${encodeURIComponent(id)}`);
        window.location.href = "/kb/articles";
      } catch (err) {
        console.error("Failed to delete article:", err);
        toastAlert.displayToast(Severity.Error, "Failed to delete article");
      }
    }
  }

  async function removeFromIndex() {
    try {
      const resp = await api.admin.delete(`api/kb/search/article/${id}`);
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Removed from bot search index");
      } else {
        toastAlert.displayToast(Severity.Error, "Could not remove from index");
      }
    } catch (err) {
      console.error("Failed to remove from index:", err);
      toastAlert.displayToast(Severity.Error, "Failed to remove from index");
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <a href="/kb/articles" className="hover:text-blue-600">
              Articles
            </a>
            <i className="fa fa-chevron-right text-xs" />
            <span className="text-slate-700 font-medium truncate max-w-xs">
              {article.title || "Untitled"}
            </span>
          </div>
          {(article as any).url && (
            <a
              href={(article as any).url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:underline flex items-center gap-1"
            >
              <i className="fa fa-external-link text-xs" />
              {(article as any).url}
            </a>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <PreviewButton title={article.title} content={article.content} />
          {article.isPublished === false && (
            <SecondaryBlueButton
              label={saving ? "Saving…" : "Save Draft"}
              disabled={saving}
              onClick={() => save(false)}
            />
          )}
          <PrimaryBlueButton
            label={saving ? "Saving…" : article.isPublished ? "Save" : "Publish"}
            disabled={saving}
            onClick={() => save(true)}
          />
          {article.isPublished && article.enableBotResponse && (
            <button
              className="px-3 py-2 text-sm font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              onClick={removeFromIndex}
              aria-label="Remove from bot search index"
              title="Remove this article from the bot search index without deleting it"
            >
              <i className="fa fa-robot" />
              <i className="fa fa-xmark ml-1 text-xs" />
            </button>
          )}
          <button
            className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            onClick={deleteArticle}
            aria-label="Delete article"
          >
            <i className="fa fa-trash" />
          </button>
        </div>
      </div>

      <ArticleForm
        article={article}
        onChange={(props) => {
          setArticle((oldState) => ({ ...oldState, ...props }));
        }}
        modelState={modelState}
      />
    </div>
  );
}
